import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import WizardStepper from "src/components/reusable/WizardStepper/WizardStepper";
import CampaignPageLayout from "src/layouts/Engagements/Campaigns/shared/CampaignPageLayout";
import CampaignFlowActions from "src/layouts/Engagements/Campaigns/shared/CampaignFlowActions";
import CampaignNextButton from "src/layouts/Engagements/Campaigns/shared/CampaignNextButton";
import { ShowFieldErrorsContext } from "src/layouts/Engagements/Campaigns/shared/UseRequiredFieldError";
import NameTagsStep from "./CreateList/steps/NameTagsStep";
import ListReadyScreen from "./CreateList/ListReadyScreen";
import { CREATE_SEGMENT_KEY, CREATE_SEGMENT_STEPS } from "./CreateSegment/CreateSegment.Constants";
import { useCreateSegment } from "./CreateSegment/UseCreateSegment";
import { useSegmentLabel } from "./CreateSegment/UseSegmentLabel";
import BuildRulesStep from "./CreateSegment/Steps/BuildRulesStep";
import SegmentReviewStep from "./CreateSegment/Steps/SegmentReviewStep";
import "./CreateList/CreateList.scss";
import "./CreateSegment/CreateSegment.scss";

const PAGE_CLASS_NAME = "create-list segment-builder";

const CreateSegment: FC = () => {
  const { t } = useTranslation();
  const label = useSegmentLabel();
  const wizard = useCreateSegment();
  const { stepIndex, stepKey, tags, preview, createdCount } = wizard;
  const isRulesStep = stepKey === "rules";

  const stepperSteps = useMemo(
    () => CREATE_SEGMENT_STEPS.map(step => ({ key: step.key, title: t(step.titleKey) })),
    [t]
  );

  const segmentMeta = useMemo(() => {
    const type = t("audienceOverview.typeSegment");
    return tags.length ? `${type} · ${label("tagsSummary", { tags: tags.join(", ") })}` : type;
  }, [t, label, tags]);

  if (createdCount !== null) {
    return (
      <CampaignPageLayout
        className={PAGE_CLASS_NAME}
        title={label("ready.pageTitle")}
        subtitle={label("ready.pageSubtitle")}
      >
        <ListReadyScreen
          listName={wizard.segmentName.trim()}
          listMeta={segmentMeta}
          peopleCount={createdCount}
          emailCount={preview?.email ?? 0}
          phoneCount={preview?.whatsapp ?? 0}
          labelKey={CREATE_SEGMENT_KEY}
        />
      </CampaignPageLayout>
    );
  }

  return (
    <CampaignPageLayout
      className={PAGE_CLASS_NAME}
      title={label("pageTitle")}
      subtitle={label("pageSubtitle")}
      stepper={<WizardStepper steps={stepperSteps} currentIndex={stepIndex} />}
    >
      <ShowFieldErrorsContext.Provider value={wizard.showFieldErrors}>
        {isRulesStep && (
          <BuildRulesStep
            rules={wizard.rules}
            preview={preview}
            previewStatus={wizard.previewStatus}
            summary={wizard.summary}
            showErrors={wizard.showFieldErrors}
            onReplaceCondition={wizard.replaceCondition}
            onChangeCategory={wizard.changeCategory}
            onAddCondition={wizard.addCondition}
            onDuplicateCondition={wizard.duplicateCondition}
            onRemoveCondition={wizard.removeCondition}
            onToggleOperator={wizard.toggleOperator}
            prompt={wizard.prompt}
          />
        )}
        {stepKey === "details" && (
          <NameTagsStep
            listName={wizard.segmentName}
            description={wizard.description}
            tags={tags}
            availableTags={wizard.availableTags}
            onListNameChange={wizard.setSegmentName}
            onDescriptionChange={wizard.setDescription}
            onAddTag={wizard.addTag}
            onRemoveTag={wizard.removeTag}
            labelKey={CREATE_SEGMENT_KEY}
          />
        )}
        {stepKey === "review" && preview && (
          <SegmentReviewStep
            segmentName={wizard.segmentName.trim()}
            segmentMeta={segmentMeta}
            preview={preview}
            summary={wizard.summary}
          />
        )}
      </ShowFieldErrorsContext.Provider>

      <CampaignFlowActions
        onBack={wizard.goBack}
        backDisabled={wizard.creating}
        className={isRulesStep ? "segment-builder__actions" : ""}
      >
        <CampaignNextButton
          canProceed={wizard.canProceed}
          onNext={wizard.goNext}
          onBlockedClick={wizard.handleBlockedNext}
        >
          {wizard.creating ? label("actions.creating") : label("actions.next")}
        </CampaignNextButton>
      </CampaignFlowActions>
    </CampaignPageLayout>
  );
};

export default CreateSegment;
